/**
 * Single source of truth for "what icon does node kind X get on the
 * canvas + palette?".
 *
 * Lucide for every CONCEPT node (if / filter / merge / loop / …) — it
 * covers them all and matches the 1.5px stroke weight we use everywhere.
 * But lucide has no concept of "Postgres": every DB integration collapsed
 * onto the same `Database` glyph, so postgres / mysql / sqlserver were
 * INDISTINGUISHABLE on the canvas (19 node kinds, one icon). Brand marks
 * are the only thing that tells them apart, so an INTEGRATION node gets
 * its real logo from `brandIcons` (the n8n model: monochrome chrome,
 * branded integrations — the break in visual system is the point, it's
 * what makes them scannable). See `brandIcons.tsx` for the licence +
 * trademark posture, and for why `s3` deliberately has NO logo.
 *
 * Three maps:
 *   - NODE_ICON      : NodeKind → lucide icon component
 *   - BRAND_ICON     : NodeKind → vendored brand logo (wins over NODE_ICON)
 *   - CATEGORY_TINT  : node category → bg+text tailwind classes
 *
 * They're consumed by the canvas's TrayNodeBox and by NodePalette so
 * both renderings agree. Adding a new node kind only needs an entry
 * here (plus the existing catalog/handler registration on the backend);
 * `NODE_ICON` is an exhaustive `Record<NodeKind, …>`, so tsc tells you.
 */
import {
  ArrowRightLeft,
  CalendarClock,
  Code2,
  Database,
  DatabaseZap,
  FileCog,
  FileInput,
  FileJson,
  FileOutput,
  FileSearch,
  Filter,
  GitBranch,
  Hand,
  HardDrive,
  HelpCircle,
  Hourglass,
  Image as ImageIcon,
  ListPlus,
  LogIn,
  LogOut,
  MapPin,
  Merge,
  MousePointerClick,
  Plug,
  Puzzle,
  Radio,
  Repeat,
  Reply,
  Route,
  Rows3,
  Send,
  Split,
  TableProperties,
  Terminal,
  Timer,
  TriangleAlert,
  Waypoints,
  Webhook,
  Workflow,
  type LucideIcon,
} from "lucide-react";

import type { ComponentType } from "react";

import {
  MysqlBrandIcon,
  PostgresBrandIcon,
  SqlServerBrandIcon,
} from "@/components/workflows/brandIcons";
import type { NodeKind } from "@/lib/api-workflows";

/**
 * The props `NodeIconBadge` passes. Both a lucide icon (stroke-based) and a
 * vendored brand logo (fill-based, ignores strokeWidth) satisfy it, so the
 * resolver can return either and the badge needs no branch.
 */
export type NodeIconComponent = ComponentType<{
  width?: number | string;
  height?: number | string;
  strokeWidth?: number | string;
}>;


export const NODE_ICON: Record<NodeKind, LucideIcon> = {
  // sources
  webhook_source: Webhook,
  sftp_source: FileInput,
  file_watch_source: FileSearch,
  db_poll_source: Database,
  http_cron_source: Timer,
  manual_source: Hand,
  // ADR-0013 — sub-workflow entry (receives the caller's items)
  workflow_input: LogIn,
  // orchestration triggers
  schedule_trigger: CalendarClock,
  webhook_trigger: Webhook,
  // ADR-0065 T1-4 — entry node of an error workflow (on-failure handler)
  error_trigger: TriangleAlert,
  // ADR-0008 PR2 — source for reactive sub-flows wired from a sink branch.
  // Same icon family as HelpCircle (it's a "hook" that activates on
  // delivery outcome) — using the existing HelpCircle keeps the palette
  // simple without pulling another lucide icon.
  delivery_trigger: HelpCircle,
  // ADR-0010 P3.3 — app triggers (fire on data-table / form / button events).
  // Passthrough SOURCE kinds, only available in WorkflowKind=app.
  row_changed: Rows3,
  form_submitted: FileInput,
  button_clicked: MousePointerClick,

  // actions — synchronous request/reply (response feeds the next node)
  http_request: ArrowRightLeft,
  // ADR-0010 P3.2 — read/write a Data Table row from inside an app workflow.
  data_table: TableProperties,
  read_write_file: HardDrive,
  // postgres integration — grouped Actions (ADR-0011); same DB icon family.
  "postgres.execute_query": Database,
  "postgres.select": Database,
  "postgres.insert": Database,
  "postgres.update": Database,
  "postgres.delete": Database,
  "postgres.upsert": Database,
  "postgres.on_new_event": Database,
  "mysql.execute_query": Database,
  "mysql.select": Database,
  "mysql.insert": Database,
  "mysql.update": Database,
  "mysql.delete": Database,
  "mysql.upsert": Database,
  "sqlserver.execute_query": Database,
  "sqlserver.select": Database,
  "sqlserver.insert": Database,
  "sqlserver.update": Database,
  "sqlserver.delete": Database,
  "sqlserver.upsert": Database,
  // s3 / object storage (ADR-0069). A GENERIC storage glyph on purpose —
  // the node speaks to ANY S3-compatible store (MinIO / R2 / B2), so an
  // AWS mark would be factually wrong (it'd imply AWS is required); see
  // brandIcons.tsx.
  "s3.upload": HardDrive,
  "s3.download": HardDrive,
  "s3.list": HardDrive,
  "s3.delete": HardDrive,
  "s3.presign": HardDrive,

  // sinks — fire-and-forget enqueue into the outbox
  http_sink: Send,
  file_sink: FileOutput,
  connector_sink: Plug,

  // transforms
  set_fields: ListPlus,
  js_code_transform: Code2,
  sandbox_code: Terminal,
  image: ImageIcon,
  extract_from_file: FileJson,

  // control flow (post-rename: branch→if, split+loop→loop_over_items, router→merge)
  filter: Filter,
  if: GitBranch,
  switch: Split,
  // "Split Out Items" (n8n Split Out) — explodes an array field into N items.
  // NOT a per-item loop (that's the real Loop node); Rows3 reads as "→ rows".
  loop_over_items: Rows3,
  // The TRUE n8n "Loop Over Items (Split In Batches)" — graphical batching loop.
  loop: Repeat,
  aggregate: Merge,
  merge: Route,
  wait: Hourglass,
  // ADR-0013 — synchronous sub-workflow call + child return marker
  call_workflow: Workflow,
  workflow_output: LogOut,
  // ADR-0038 P3 — fire an ETL pipeline from a workflow
  run_etl_pipeline: Waypoints,
  // respond_to_webhook lives in CONTROL: it's a passthrough with a
  // side-effect (closes the loop with the HTTP caller) — items still
  // flow downstream, so the canvas renders an output handle.
  respond_to_webhook: Reply,

  // canonical I/O
  read_canonical: DatabaseZap,
  write_canonical: FileCog,
  emit_event: Radio,

  // lookups
  call_mapping: MapPin,

  // plugins
  plugin_call: Puzzle,
};


/**
 * Real brand logos for the INTEGRATION nodes — they WIN over `NODE_ICON`.
 *
 * Only true third-party products belong here (the mark identifies the thing
 * the node talks to). A concept node stays lucide. `s3` is deliberately
 * absent: it's S3-COMPATIBLE storage, not AWS. See `brandIcons.tsx`.
 */
export const BRAND_ICON: Partial<Record<NodeKind, NodeIconComponent>> = {
  "postgres.execute_query": PostgresBrandIcon,
  "postgres.select": PostgresBrandIcon,
  "postgres.insert": PostgresBrandIcon,
  "postgres.update": PostgresBrandIcon,
  "postgres.delete": PostgresBrandIcon,
  "postgres.upsert": PostgresBrandIcon,
  "postgres.on_new_event": PostgresBrandIcon,
  "mysql.execute_query": MysqlBrandIcon,
  "mysql.select": MysqlBrandIcon,
  "mysql.insert": MysqlBrandIcon,
  "mysql.update": MysqlBrandIcon,
  "mysql.delete": MysqlBrandIcon,
  "mysql.upsert": MysqlBrandIcon,
  "sqlserver.execute_query": SqlServerBrandIcon,
  "sqlserver.select": SqlServerBrandIcon,
  "sqlserver.insert": SqlServerBrandIcon,
  "sqlserver.update": SqlServerBrandIcon,
  "sqlserver.delete": SqlServerBrandIcon,
  "sqlserver.upsert": SqlServerBrandIcon,
};


/** Returns the icon for a node kind — the brand logo when the kind is a
 *  third-party integration, else its lucide glyph, falling back to a
 *  generic "?" so an unmapped kind never crashes the render. */
export function iconForKind(kind: NodeKind | undefined): NodeIconComponent {
  if (!kind) return HelpCircle;
  return BRAND_ICON[kind] ?? NODE_ICON[kind] ?? HelpCircle;
}


/** Tailwind background + text classes for the small icon badge,
 *  one per category. The hue matches the historical CATEGORY_BG palette
 *  so users who already knew "source=green, sink=red" don't have to
 *  re-learn anything — but it now lives ONLY on the small icon square,
 *  not the whole node body. Soft tints work on light AND dark mode. */
export const CATEGORY_TINT: Record<string, string> = {
  source:       "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  sink:         "bg-rose-500/15    text-rose-600    dark:text-rose-400",
  transform:    "bg-sky-500/15     text-sky-600     dark:text-sky-400",
  control:      "bg-amber-500/15   text-amber-600   dark:text-amber-400",
  // Teal differentiates ACTION from SINK (rose) and LOOKUP (zinc).
  // Picked teal because it reads as "active call" without competing
  // with the warning amber of CONTROL.
  action:       "bg-teal-500/15    text-teal-600    dark:text-teal-400",
  io_canonical: "bg-violet-500/15  text-violet-600  dark:text-violet-400",
  lookup:       "bg-zinc-500/15    text-zinc-600    dark:text-zinc-300",
  plugin:       "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400",
};


export function tintForCategory(category: string | undefined): string {
  if (!category) return "bg-muted text-muted-foreground";
  return CATEGORY_TINT[category] ?? "bg-muted text-muted-foreground";
}


/** Reusable badge with the icon for a given (kind, category). Used in
 *  both the canvas chip and the palette row so they look identical.
 *  ``size`` controls the outer square; the icon scales accordingly. */
export function NodeIconBadge({
  kind,
  category,
  size = 28,
}: {
  kind: NodeKind | undefined;
  category: string | undefined;
  size?: number;
}) {
  const Icon = iconForKind(kind);
  const tint = tintForCategory(category);
  const iconSize = Math.max(12, Math.floor(size * 0.55));
  return (
    <span
      className={
        "inline-flex shrink-0 items-center justify-center rounded-md " + tint
      }
      style={{ width: size, height: size }}
      aria-hidden
    >
      <Icon width={iconSize} height={iconSize} strokeWidth={1.75} />
    </span>
  );
}


/** Hourglass kept as an explicit export for the "running" status —
 *  not used here but handy for callers that want the same vocabulary. */
export { Hourglass };
